/**
 * HandHistoryStrip - Recent hands as keep/mulligan pips with a running tally
 *
 * Clicking a pip restores that hand so you can revisit a decision.
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';

import type { SimCard } from '@/features/decks/lib/handSim';

// ============================================================================
// Types
// ============================================================================

export interface HandHistoryEntry {
  id: number;
  hand: SimCard[];
  mulliganCount: number;
  outcome: 'kept' | 'mulliganed';
}

export interface HandHistoryStripProps {
  entries: HandHistoryEntry[];
  activeId: number | null;
  onSelect: (entry: HandHistoryEntry) => void;
}

export const HAND_HISTORY_LIMIT = 10;

// ============================================================================
// Component
// ============================================================================

export default function HandHistoryStrip({ entries, activeId, onSelect }: HandHistoryStripProps): ReactElement | null {
  if (entries.length === 0) return null;

  const kept = entries.filter((entry) => entry.outcome === 'kept').length;

  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-1" role="group" aria-label="Recent hands">
        {entries.map((entry) => (
          <button
            key={entry.id}
            type="button"
            onClick={() => onSelect(entry)}
            aria-label={`Restore hand ${entry.id} (${entry.outcome}${entry.mulliganCount > 0 ? `, mulliganed to ${7 - entry.mulliganCount}` : ''})`}
            aria-current={entry.id === activeId}
            className={clsx(
              'h-2.5 w-2.5 rounded-full transition-transform hover:scale-125 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none',
              entry.outcome === 'kept' ? 'bg-emerald-500' : 'bg-amber-500',
              entry.id === activeId ? 'ring-2 ring-zinc-300 ring-offset-1 ring-offset-zinc-900' : 'opacity-70',
            )}
          />
        ))}
      </div>
      <span className="text-ash shrink-0 text-[11px]">
        kept {kept}/{entries.length}
      </span>
    </div>
  );
}
